"use client";

import React, { useState, useEffect } from "react";
import {
  DownloadCloudIcon,
  Edit,
  Plus,
  Trash2,
  TrashIcon,
  UploadIcon,
  X,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Search,
} from "lucide-react";
import BookingTable from "../common/Table";
import DynamicForm from "./Popup";
import Button from "./Button";
import { 
  useMasterAirport, 
  useUpdateStatus, 
  getCityByname,
  useAddAirportRailway,
  useUpdateAirportRailway,
  useUpdateAirportDisplayOrder 
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";

interface Airport {
  id: number;
  city_id: string;
  city_name: string;
  state_name: string;
  country_name: string;
  pickup_type: string;
  airport_railway_name: string;
  zone: string;
  meeting_point: string;
  latitude?: string;
  longitude?: string;
  display_order: number;
  created_by: string;
  status: number;
}

const AirportMaster: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAirport, setCurrentAirport] = useState<Airport | null>(null);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [filteredAirports, setFilteredAirports] = useState<Airport[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  
  // Search states
  const [searchAirportRailwayName, setSearchAirportRailwayName] = useState("");
  const [searchStatus, setSearchStatus] = useState<number | "">("");
  
  // City search states for search form
  const [searchCities, setSearchCities] = useState<any[]>([]);
  const [searchCityTerm, setSearchCityTerm] = useState("");
  const [debouncedSearchCityTerm, setDebouncedSearchCityTerm] = useState("");
  const [selectedSearchCity, setSelectedSearchCity] = useState<{ id: string; name: string } | null>(null);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  
  // City search states for add/edit form
  const [formCities, setFormCities] = useState<any[]>([]);
  const [formCityTerm, setFormCityTerm] = useState("");
  const [debouncedFormCityTerm, setDebouncedFormCityTerm] = useState("");
  const [selectedFormCity, setSelectedFormCity] = useState<{ id: string; name: string } | null>(null);
  const [showFormDropdown, setShowFormDropdown] = useState(false);

  // API Hooks
  const masterAirport = useMasterAirport();
  const updateStatus = useUpdateStatus();
  const addAirportRailway = useAddAirportRailway();
  const updateAirportRailway = useUpdateAirportRailway();
  const updateDisplayOrderMutation = useUpdateAirportDisplayOrder();
  const searchCitiesQuery = getCityByname(debouncedSearchCityTerm);
  const formCitiesQuery = getCityByname(debouncedFormCityTerm);

  const user = useAuth();

  // Debug logging
  const debugLog = (message: string, data?: any) => {
    console.log(`[AirportMaster Debug] ${message}`, data || '');
  };

  // Fetch airports on component mount and sort by display_order
  useEffect(() => {
    fetchAirports();
  }, []);

  // Apply filtering whenever airports or search criteria change
  useEffect(() => {
    applyFilters();
  }, [airports, searchAirportRailwayName, searchStatus, selectedSearchCity]);

  const fetchAirports = (searchParams?: any) => {
    masterAirport.mutate(
      searchParams || {},
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            // Sort by display_order in ascending order
            const sortedData = [...data.data].sort((a, b) => a.display_order - b.display_order);
            setAirports(sortedData);
            debugLog('Airports fetched and sorted', sortedData);
          }
        },
        onError: (error) => {
          console.error("Error fetching airports:", error);
        },
      }
    );
  };

  // Apply filters to airports
  const applyFilters = () => {
    let filtered = [...airports];

    // Apply city filter
    if (selectedSearchCity?.id) {
      filtered = filtered.filter(airport => 
        airport.city_id.toString() === selectedSearchCity.id
      );
    }

    // Apply airport/railway name filter
    if (searchAirportRailwayName) {
      filtered = filtered.filter(airport => 
        airport.airport_railway_name.toLowerCase()
          .includes(searchAirportRailwayName.toLowerCase())
      );
    }

    // Apply status filter
    if (searchStatus !== "") {
      filtered = filtered.filter(airport => 
        airport.status === searchStatus
      );
    }

    setFilteredAirports(filtered);
  };

  // Handle drag and drop reordering using the new swap hook
  const handleDisplayOrderReorder = async (reorderedData: any[]) => {
    // Prevent multiple simultaneous reorder operations
    if (isReordering) {
      debugLog('⏳ Reorder already in progress, skipping');
      return;
    }

    debugLog('🔄 Reordering started', reorderedData);
    
    setIsReordering(true);
    setIsUpdatingOrder(true);

    try {
      // For swap functionality, we need to identify which two records were swapped
      // The table gives us the new order, we need to compare with old order
      
      // Get IDs from reordered data
      const reorderedIds = reorderedData.map(row => row.originalData.id);
      
      // Find which position changed compared to filteredAirports
      let draggedId: number | null = null;
      let targetId: number | null = null;
      let cityId: number | null = null;

      // Simple approach: Assume the first item that moved is what we want
      // Better approach: Compare with previous order
      if (reorderedData.length > 0) {
        // Get the first two IDs that are in reordered position
        if (reorderedData.length >= 2) {
          draggedId = reorderedData[0].originalData.id;
          targetId = reorderedData[1].originalData.id;
          
          // Get city_id from either record (should be same for swap)
          cityId = parseInt(reorderedData[0].originalData.city_id);
          
          debugLog('Swap attempt', {
            draggedId,
            targetId,
            cityId,
            draggedCity: reorderedData[0].originalData.city_name,
            targetCity: reorderedData[1].originalData.city_name
          });
        }
      }

      if (!draggedId || !targetId || !cityId) {
        debugLog('Cannot determine swap IDs');
        alert("Unable to determine which records to swap. Please try again.");
        setIsReordering(false);
        setIsUpdatingOrder(false);
        return;
      }

      // Check if both airports belong to the same city
      const draggedAirport = reorderedData[0].originalData;
      const targetAirport = reorderedData[1]?.originalData;
      
      if (draggedAirport.city_id !== targetAirport?.city_id) {
        alert("Cannot swap airports from different cities. Please drag within the same city.");
        setIsReordering(false);
        setIsUpdatingOrder(false);
        return;
      }

      // Get logged in user
      const loggedInUser = user?.user?.id;

      // Call the swap API
      updateDisplayOrderMutation.mutate(
        {
          city_id: cityId,
          id1: draggedId,
          id2: targetId,
          user_id: loggedInUser,
        },
        {
          onSuccess: (data) => {
            if (data.status === "success") {
              debugLog('✅ Display orders swapped successfully via API', data);
              
              // Update local state with swapped orders
              const updatedAirports = [...airports];
              const draggedIndex = updatedAirports.findIndex(a => a.id === draggedId);
              const targetIndex = updatedAirports.findIndex(a => a.id === targetId);
              
              if (draggedIndex !== -1 && targetIndex !== -1) {
                // Swap display orders
                const tempOrder = updatedAirports[draggedIndex].display_order;
                updatedAirports[draggedIndex].display_order = updatedAirports[targetIndex].display_order;
                updatedAirports[targetIndex].display_order = tempOrder;
                
                // Sort by display order
                updatedAirports.sort((a, b) => a.display_order - b.display_order);
                setAirports(updatedAirports);
              }
              
              alert("Display order updated successfully!");
            } else {
              debugLog('❌ API swap failed', data);
              alert(data.message || "Failed to update display orders.");
              // Revert to original data on API failure
              fetchAirports();
            }
          },
          onError: (error) => {
            console.error("Error swapping display orders:", error);
            alert("Failed to update display orders. Please try again.");
            // Revert to original data on error
            fetchAirports();
          },
          onSettled: () => {
            setIsUpdatingOrder(false);
            setIsReordering(false);
          }
        }
      );

    } catch (error) {
      console.error("Error in reordering:", error);
      alert("Failed to update display orders. Please try again.");
      // Revert to original data on error
      fetchAirports();
      setIsUpdatingOrder(false);
      setIsReordering(false);
    }
  };

  // Fixed: Debounce search term for city search in SEARCH form
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchCityTerm.length >= 3) {
        setDebouncedSearchCityTerm(searchCityTerm);
        setShowSearchDropdown(true);
      } else {
        setDebouncedSearchCityTerm("");
        setSearchCities([]);
        setShowSearchDropdown(false);
      }
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchCityTerm]);

  // Fixed: Debounce search term for city search in ADD/EDIT form
  useEffect(() => {
    const handler = setTimeout(() => {
      if (formCityTerm.length >= 3) {
        setDebouncedFormCityTerm(formCityTerm);
        setShowFormDropdown(true);
      } else {
        setDebouncedFormCityTerm("");
        setFormCities([]);
        setShowFormDropdown(false);
      }
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [formCityTerm]);

  // Update cities when debounced search term changes for SEARCH form
  useEffect(() => {
    if (debouncedSearchCityTerm && searchCitiesQuery.data) {
      const citiesData = searchCitiesQuery.data?.data;
      if (citiesData && Array.isArray(citiesData)) {
        setSearchCities(citiesData);
      } else {
        setSearchCities([]);
      }
    } else {
      setSearchCities([]);
    }
  }, [debouncedSearchCityTerm, searchCitiesQuery.data]);

  // Update cities when debounced search term changes for ADD/EDIT form
  useEffect(() => {
    if (debouncedFormCityTerm && formCitiesQuery.data) {
      const citiesData = formCitiesQuery.data?.data;
      if (citiesData && Array.isArray(citiesData)) {
        setFormCities(citiesData);
      } else {
        setFormCities([]);
      }
    } else {
      setFormCities([]);
    }
  }, [debouncedFormCityTerm, formCitiesQuery.data]);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (!isModalOpen) {
      setCurrentAirport(null);
      setSelectedFormCity(null);
      setFormCityTerm("");
      setFormCities([]);
      setShowFormDropdown(false);
    } else if (currentAirport) {
      setSelectedFormCity({
        id: currentAirport.city_id,
        name: currentAirport.city_name,
      });
      setFormCityTerm(currentAirport.city_name);
    }
  }, [isModalOpen, currentAirport]);

  const formFields: any = [
    {
      id: "city_search",
      label: "City *",
      type: "searchselect",
      required: true,
      defaultValue: formCityTerm,
      placeholder: "Type at least 3 characters to search for city...",
      options: formCities.map((city) => ({
        value: city.city_id.toString(),
        label: `${city.city_name}, ${city.state_name}`,
      })),
      loading: formCitiesQuery.isLoading,
      onSearchChange: (value: string) => setFormCityTerm(value),
      onOptionSelect: (value: string, label: string) => {
        const selectedCityData = formCities.find(city => city.city_id.toString() === value);
        if (selectedCityData) {
          setSelectedFormCity({ 
            id: value, 
            name: selectedCityData.city_name 
          });
          setFormCityTerm(selectedCityData.city_name);
        }
        setFormCities([]);
        setShowFormDropdown(false);
      },
      selectedValue: selectedFormCity
        ? { value: selectedFormCity.id, label: selectedFormCity.name }
        : null,
      showDropdown: showFormDropdown,
      onFocus: () => {
        if (formCityTerm.length >= 3 && formCities.length > 0) {
          setShowFormDropdown(true);
        }
      },
      onBlur: () => {
        setTimeout(() => setShowFormDropdown(false), 200);
      }
    },
    {
      id: "pickup_type",
      label: "Pickup Type *",
      type: "select",
      required: true,
      defaultValue: currentAirport ? currentAirport.pickup_type : "DEPARTURE",
      options: [
        { value: "DEPARTURE", label: "Departure" },
        { value: "ARRIVAL", label: "Arrival" },
      ],
    },
    {
      id: "airport_railway_name",
      label: "Airport/Railway Station Name *",
      type: "text",
      required: true,
      defaultValue: currentAirport
        ? currentAirport.airport_railway_name
        : "",
      placeholder: "Enter name",
    },
    {
      id: "zone",
      label: "Airport/Railway Zone *",
      type: "text",
      required: true,
      defaultValue: currentAirport
        ? currentAirport.zone
        : "",
      placeholder: "Enter zone",
    },
    {
      id: "meeting_point",
      label: "Meeting Point *",
      type: "text",
      required: true,
      defaultValue: currentAirport ? currentAirport.meeting_point : "",
      placeholder: "Enter meeting point",
    },
    {
      id: "latitude",
      label: "Latitude",
      type: "text",
      required: false,
      defaultValue: currentAirport ? currentAirport.latitude || "" : "",
      placeholder: "Enter latitude (optional)",
    },
    {
      id: "longitude",
      label: "Longitude",
      type: "text",
      required: false,
      defaultValue: currentAirport ? currentAirport.longitude || "" : "",
      placeholder: "Enter longitude (optional)",
    },
    {
      id: "display_order",
      label: "Display Order *",
      type: "number",
      required: true,
      defaultValue: currentAirport ? currentAirport.display_order : "",
      placeholder: "Enter display order",
      min: 1,
      helpText: currentAirport 
        ? "Lower numbers appear first. Change this to reorder within the same city."
        : "Lower numbers appear first. Will be automatically set to last position if not specified."
    }
  ];

  const handleFormSubmit = async (formData: any) => {
    const loggedInUser = user?.user?.id;

    // Validate city selection
    if (!selectedFormCity) {
      alert("Please select a city");
      return;
    }

    // Validate display order
    if (!formData.display_order || formData.display_order < 1) {
      alert("Please enter a valid display order (minimum 1)");
      return;
    }

    try {
      const basePayload = {
        city_id: selectedFormCity.id,
        pickup_type: formData.pickup_type,
        airport_railway_name: formData.airport_railway_name,
        meeting_point: formData.meeting_point,
        zone: formData.zone,
        latitude: formData.latitude || "",
        longitude: formData.longitude || "",
        display_order: parseInt(formData.display_order),
        ip: "127.0.0.1",
        created_by: loggedInUser,
        modified_by: loggedInUser,
      };

      if (currentAirport) {
        // Update existing airport
        updateAirportRailway.mutate(
          {
            ...basePayload,
            id: currentAirport.id,
            status: currentAirport.status,
          },
          {
            onSuccess: (data) => {
              if (data.status === "success") {
                fetchAirports();
                setIsModalOpen(false);
                setCurrentAirport(null);
                alert("Airport/Railway updated successfully!");
              } else {
                alert(data.message || "Failed to update airport. Please try again.");
              }
            },
            onError: (error) => {
              console.error("Update error:", error);
              alert("Failed to update airport. Please try again.");
            },
          }
        );
      } else {
        // Add new airport
        debugLog('Adding new airport with display order', basePayload.display_order);
        
        addAirportRailway.mutate(
          {
            ...basePayload,
            status: 1,
          },
          {
            onSuccess: (data) => {
              if (data.status === "success") {
                fetchAirports();
                setIsModalOpen(false);
                alert("Airport/Railway added successfully!");
              } else {
                alert(data.message || "Failed to add airport. Please try again.");
              }
            },
            onError: (error) => {
              console.error("Add error:", error);
              alert("Failed to add airport. Please try again.");
            },
          }
        );
      }
    } catch (error) {
      console.error("Form submission error:", error);
      alert("An error occurred. Please try again.");
    }
  };

  const handlePublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: user?.user?.id.toString(),
        airport_status: 1,
      },
      {
        onSuccess: (data) => {
          if (data.airport_status === "success") {
            fetchAirports();
            setSelectedRows([]);
          }
        },
      }
    );
  };

  const handleUnpublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: user?.user?.id.toString(),
        airport_status: 0,
      },
      {
        onSuccess: (data) => {
          if (data.airport_status === "success") {
            fetchAirports();
            setSelectedRows([]);
          }
        },
      }
    );
  };

  const handleDelete = () => {
    if (selectedRows.length === 0) return;

    if (confirm("Are you sure you want to delete selected airports?")) {
      // You might need to implement a delete mutation if available
      // For now, we'll just clear selection
      setSelectedRows([]);
    }
  };

  const handleSearch = () => {
    // Apply filters locally without fetching from API
    setIsSearchExpanded(false); // Collapse search form after search
    
    // Build search parameters
    const searchParams: any = {};
    
    if (selectedSearchCity?.id) {
      searchParams.city_id = selectedSearchCity.id;
    }
    
    if (searchAirportRailwayName) {
      searchParams.airport_name = searchAirportRailwayName;
    }
    
    if (searchStatus !== "") {
      searchParams.status = searchStatus;
    }

    // If we have any search criteria, fetch from API
    if (selectedSearchCity?.id || searchAirportRailwayName || searchStatus !== "") {
      masterAirport.mutate(
        searchParams,
        {
          onSuccess: (data) => {
            if (data.status === "success") {
              // Sort by display_order in ascending order
              const sortedData = [...data.data].sort((a, b) => a.display_order - b.display_order);
              setAirports(sortedData);
              debugLog('Search results fetched and sorted', sortedData);
            }
          },
          onError: (error) => {
            console.error("Error searching airports:", error);
          },
        }
      );
    } else {
      // If no search criteria, fetch all data
      fetchAirports();
    }
  };

  const handleClear = () => {
    setSearchCityTerm("");
    setSelectedSearchCity(null);
    setSearchCities([]);
    setSearchAirportRailwayName("");
    setSearchStatus("");
    setShowSearchDropdown(false);
    // Reset to fetch all data
    fetchAirports();
  };

  // Handle city selection in search form
  const handleSearchCitySelect = (city: any) => {
    setSelectedSearchCity({ 
      id: city.city_id.toString(), 
      name: city.city_name 
    });
    setSearchCityTerm(city.city_name);
    setSearchCities([]);
    setShowSearchDropdown(false);
  };

  // Get the appropriate loading state for form submission
  const getFormLoadingState = () => {
    return currentAirport ? updateAirportRailway.isPending : addAirportRailway.isPending;
  };

  // Transform API data to match table structure
  const tableData = filteredAirports.map((item, index) => ({
    sno: index + 1,
    id: item.id,
    cityName: item.city_name,
    stateName: item.state_name,
    countryName: item.country_name,
    pickupType: item.pickup_type,
    airportRailwayStationName: item.airport_railway_name,
    zone: item.zone,
    meetingPoint: item.meeting_point,
    createdBy: item.created_by,
    displayOrder: item.display_order,
    status: item.status,
    originalData: item,
  }));

  const columns = [
    {
      key: "drag",
      header: "Drag",
      sortable: false,
      render: (_: any, row: any, index: number) => (
        <div
          draggable
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 transition-colors"
          title="Drag to reorder (swap with another row)"
        >
          <GripVertical size={16} />
        </div>
      ),
    },
    {
      key: "selection",
      header: "",
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, row.id]);
            } else {
              setSelectedRows(selectedRows.filter((id) => id !== row.id));
            }
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    { key: "sno", header: "S.No", sortable: true },
    { key: "displayOrder", header: "Display Order", sortable: true },
    { key: "cityName", header: "City Name", sortable: true },
    { key: "stateName", header: "State Name", sortable: true },
    { key: "countryName", header: "Country Name", sortable: true },
    { key: "pickupType", header: "Pickup Type", sortable: true },
    {
      key: "airportRailwayStationName",
      header: "Airport/Railway Station Name",
      sortable: true,
      width: "200px",
    },
    {
      key: "meetingPoint",
      header: "Meeting Point",
      sortable: true,
      width: "200px",
    },
    { key: "createdBy", header: "Created By", sortable: true },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (item: any) => {
        return (
          <div className="flex items-center">
            <div
              className={`w-3 h-3 rounded-full mr-2 ${
                item === 1 ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <span className="text-xs">
              {item === 1 ? "Active" : "Inactive"}
            </span>
          </div>
        );
      },
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentAirport(row.originalData);
                setIsModalOpen(true);
              }}
              title="Edit"
            >
              <Edit size={15} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => {
                if (confirm("Are you sure you want to delete this airport record?")) {
                  setSelectedRows([row.id]);
                  handleDelete();
                }
              }}
              title="Delete"
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  // Select all checkbox handler
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(tableData.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
      {/* Collapsable Search Form Header */}
      <div className="mb-6">
        <button
          onClick={() => setIsSearchExpanded(!isSearchExpanded)}
          className="flex items-center justify-between w-full p-4 bg-gray-50 rounded-lg shadow-sm border border-gray-200 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">
              Search Airport/Railway Stations
            </span>
            {(selectedSearchCity || searchAirportRailwayName || searchStatus !== "") && (
              <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                {[selectedSearchCity ? 1 : 0, searchAirportRailwayName ? 1 : 0, searchStatus !== "" ? 1 : 0].reduce((a, b) => a + b)} filters active
              </span>
            )}
          </div>
          {isSearchExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-600" />
          )}
        </button>

        {/* Expandable Search Form */}
        {isSearchExpanded && (
          <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* City Search */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">City</label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchCityTerm}
                    onChange={(e) => setSearchCityTerm(e.target.value)}
                    onFocus={() => {
                      if (searchCityTerm.length >= 3 && searchCities.length > 0) {
                        setShowSearchDropdown(true);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowSearchDropdown(false), 200);
                    }}
                    className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full h-[32px]"
                    placeholder="Type to search city..."
                  />
                  {/* Dropdown for city search results */}
                  {showSearchDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {searchCitiesQuery.isLoading ? (
                        <div className="px-3 py-2 text-sm text-gray-500 flex items-center justify-center">
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                          Searching...
                        </div>
                      ) : searchCities.length > 0 ? (
                        searchCities.map((city) => (
                          <div
                            key={city.city_id}
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 hover:text-blue-700 transition-colors"
                            onClick={() => handleSearchCitySelect(city)}
                          >
                            {city.city_name}, {city.state_name}
                          </div>
                        ))
                      ) : debouncedSearchCityTerm ? (
                        <div className="px-3 py-2 text-sm text-gray-500">No cities found</div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>

              {/* Airport/Railway Station Search */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Airport/Railway Station
                </label>
                <input
                  type="text"
                  value={searchAirportRailwayName}
                  onChange={(e) => setSearchAirportRailwayName(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full h-[32px]"
                  placeholder="Enter station name"
                />
              </div>

              {/* Status Search */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">Status</label>
                <select
                  value={searchStatus}
                  onChange={(e) => setSearchStatus(e.target.value === "" ? "" : Number(e.target.value))}
                  className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full h-[32px]"
                >
                  <option value="">All Status</option>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex items-end gap-2">
                <Button
                  size="sm"
                  onClick={handleSearch}
                  icon={<Search className="w-4 h-4" />}
                  iconPosition="left"
                  disabled={masterAirport.isPending}
                  className="w-full"
                >
                  {masterAirport.isPending ? "Searching..." : "Search"}
                </Button>

                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleClear}
                  className="w-full"
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-5">
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-600">
            {isUpdatingOrder && (
              <div className="flex items-center text-blue-600">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                Updating display order...
              </div>
            )}
            <div className="mt-1 text-xs text-gray-500">
              Showing {filteredAirports.length} of {airports.length} records
              {selectedSearchCity && (
                <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded">
                  Filtered by: {selectedSearchCity.name}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                setCurrentAirport(null);
                setIsModalOpen(true);
              }}
              icon={<Plus className="w-4 h-4" />}
              iconPosition="left"
            >
              Add Airport/Railway Station
            </Button>

            <Button
              size="sm"
              variant="secondary"
              onClick={handlePublish}
              icon={<UploadIcon className="w-4 h-4" />}
              iconPosition="left"
              disabled={selectedRows.length === 0 || updateStatus.isPending}
            >
              {updateStatus.isPending ? "Publishing..." : "Publish"}
            </Button>

            <Button
              size="sm"
              variant="warning"
              onClick={handleUnpublish}
              icon={<DownloadCloudIcon className="w-4 h-4" />}
              iconPosition="left"
              disabled={selectedRows.length === 0 || updateStatus.isPending}
            >
              {updateStatus.isPending ? "Unpublishing..." : "Unpublish"}
            </Button>

            <Button
              size="sm"
              variant="danger"
              onClick={handleDelete}
              icon={<TrashIcon className="w-4 h-4" />}
              iconPosition="left"
              disabled={selectedRows.length === 0}
            >
              Delete
            </Button>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <BookingTable
            data={tableData}
            columns={columns}
            heading={[]}
            searchable={false}
            filterable={false}
            sortable={true}
            selectable={true}
            onSelectionChange={setSelectedRows}
            loading={masterAirport.isPending}
            dragDrop={{
              enabled: true,
              onReorder: handleDisplayOrderReorder,
              dragHandleRenderer: (row, index) => (
                <div
                  draggable
                  className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 transition-colors"
                  title="Drag to swap with another row (same city only)"
                >
                  <GripVertical size={16} />
                </div>
              )
            }}
            renderHeader={() => (
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                    Drag
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                    <input
                      type="checkbox"
                      checked={
                        selectedRows.length === tableData.length &&
                        tableData.length > 0
                      }
                      onChange={handleSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    S.No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Display Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    City Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    State Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Country Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pickup Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Airport/Railway Station Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Meeting Point
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
            )}
          />
        </div>

        <div className="mt-4 text-sm text-gray-500 flex items-center">
          <GripVertical size={16} className="mr-2 text-gray-400" />
          <span>
            {selectedSearchCity 
              ? `Drag and drop rows to swap display order within ${selectedSearchCity.name}`
              : "Filter by a city first, then drag and drop to swap display orders"
            }
          </span>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
            }}
          />

          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  {currentAirport ? "Update Airport" : "Add Airport"}
                </h2>

                <button
                  onClick={() => {
                    setIsModalOpen(false);
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>

              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <DynamicForm
                  fields={formFields}
                  onSubmit={handleFormSubmit}
                  submitText={currentAirport ? "Update Airport" : "Add Airport"}
                  loading={getFormLoadingState()}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AirportMaster;